import React from 'react';
import { theme } from '../styles/theme';
import { Footer } from '../components/Footer';
import { BookIcon, EditIcon, UsersIcon } from '../components/Icons';
import { WaitlistModal } from '../components/WaitlistModal';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
  icon: React.ReactNode;
}

const canonicalBlogUrl = 'https://manuscriptmatch.com/blog';
const socialImageUrl = 'https://manuscriptmatch.com/social-preview.png';
const socialImageAlt = 'Manuscript Match: the right readers for your next draft. In development; join the waitlist.';

export const BlogPage = () => {
  // Update meta tags for SEO
  React.useEffect(() => {
    // Production HTML already includes complete route-specific metadata.
    if (document.getElementById('marketing-structured-data') &&
        document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href === canonicalBlogUrl) return;

    // Update page title
    document.title = 'Writing & Beta Reading Guides | Manuscript Match Blog';

    // Update or create meta tags
    const updateOrCreateMeta = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const description = 'Practical guides to beta reading, manuscript feedback, query letters, and preparing your next draft from Manuscript Match.';

    // Standard meta tags
    updateOrCreateMeta('description', description);
    updateOrCreateMeta('keywords', 'beta readers, manuscript feedback, writing tips, literary agents, query letters, author platform, beta reading');

    // Open Graph tags
    updateOrCreateMeta('og:title', 'Manuscript Match Blog', true);
    updateOrCreateMeta('og:description', description, true);
    updateOrCreateMeta('og:type', 'website', true);
    updateOrCreateMeta('og:url', canonicalBlogUrl, true);
    updateOrCreateMeta('og:site_name', 'Manuscript Match', true);
    updateOrCreateMeta('og:image', socialImageUrl, true);
    updateOrCreateMeta('og:image:alt', socialImageAlt, true);

    // Twitter Card tags
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:image', socialImageUrl);
    updateOrCreateMeta('twitter:image:alt', socialImageAlt);
    updateOrCreateMeta('twitter:title', 'Manuscript Match Blog');
    updateOrCreateMeta('twitter:description', description);

    // Add JSON-LD structured data for blog
    let structuredData = document.querySelector('#blog-structured-data') as HTMLScriptElement;
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'blog-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      'name': 'Manuscript Match Blog',
      'description': description,
      'publisher': {
        '@type': 'Organization',
        'name': 'Manuscript Match',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://manuscriptmatch.com/ManuscriptMatchLogo.png'
        }
      },
      'url': canonicalBlogUrl
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalBlogUrl;
  }, []);

  const blogPosts: BlogPost[] = [
    {
      id: '7',
      slug: 'beta-reader-brief-template',
      title: "Beta Reader Brief Template: What to Send With Your Manuscript",
      excerpt: "Prepare your manuscript for beta readers with a clear brief, a practical checklist and five feedback questions. Includes a free editable template.",
      date: 'October 30, 2026',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '7 min read',
      icon: <EditIcon />,
    },
    {
      id: '1',
      slug: 'the-power-of-beta-readers-how-feedback-shapes-great-stories',
      title: 'The Power of Beta Readers: How Feedback Shapes Great Stories',
      excerpt: 'Discover how beta readers can transform your manuscript from good to great. Learn what makes constructive feedback valuable and how to find readers who truly understand your genre.',
      date: 'November 8, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '5 min read',
      icon: <UsersIcon />,
    },
    {
      id: '2',
      slug: 'building-your-author-platform-before-publication',
      title: 'Building Your Author Platform Before Publication',
      excerpt: 'Starting early can make all the difference. Explore strategies for building an engaged audience while you\'re still in the manuscript phase, and why literary agents care about your platform.',
      date: 'November 5, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '7 min read',
      icon: <EditIcon />,
    },
    {
      id: '3',
      slug: 'what-literary-agents-really-look-for-in-query-letters',
      title: 'What Literary Agents Really Look For in Query Letters',
      excerpt: 'Get insider tips on crafting query letters that stand out. We break down the essential elements that catch an agent\'s eye and common mistakes to avoid.',
      date: 'November 9, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '6 min read',
      icon: <BookIcon />,
    },
    {
      id: '4',
      slug: 'how-to-give-meaningful-beta-reader-feedback',
      title: 'How to Give Meaningful Beta Reader Feedback',
      excerpt: 'Being a beta reader is about more than just spotting typos. Learn how to provide thoughtful, constructive criticism that helps writers improve their craft while staying encouraging.',
      date: 'November 7, 2025',
      author: 'Manuscript Match Team',
      category: 'For Beta Readers',
      readTime: '5 min read',
      icon: <BookIcon />,
    },
    {
      id: '5',
      slug: 'the-beta-reading-process-what-to-expect',
      title: 'The Beta Reading Process: What to Expect',
      excerpt: 'New to beta reading? This guide walks you through the entire process, from selecting manuscripts to submitting feedback, and how to build meaningful connections with authors.',
      date: 'November 5, 2025',
      author: 'Manuscript Match Team',
      category: 'For Beta Readers',
      readTime: '4 min read',
      icon: <UsersIcon />,
    },
    {
      id: '6',
      slug: 'common-manuscript-mistakes-and-how-to-fix-them',
      title: 'Common Manuscript Mistakes and How to Fix Them',
      excerpt: 'Learn about the most frequent issues found in unpublished manuscripts and practical strategies to address them before submitting to agents or beta readers.',
      date: 'November 10, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '8 min read',
      icon: <EditIcon />,
    },
  ];

  const categories = ['All', 'For Writers', 'For Beta Readers', 'For Literary Agents'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.textPrimary,
      fontFamily: theme.fonts.primary,
    }}>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .blog-header {
            padding: 16px 20px !important;
          }
          .blog-logo {
            width: 32px !important;
            height: 32px !important;
          }
          .blog-logo-text {
            font-size: 18px !important;
          }
          .blog-header-link {
            font-size: 14px !important;
          }
          .blog-hero {
            padding: 48px 24px !important;
          }
          .blog-hero-title {
            font-size: 36px !important;
            margin-bottom: 16px !important;
          }
          .blog-hero-subtitle {
            font-size: 16px !important;
            line-height: 1.5 !important;
          }
          .blog-content {
            padding: 48px 24px !important;
          }
          .blog-filters {
            gap: 8px !important;
            flex-wrap: wrap !important;
          }
          .blog-filter-btn {
            padding: 8px 16px !important;
            font-size: 13px !important;
          }
          .blog-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .blog-card {
            padding: 24px !important;
          }
          .blog-card-title {
            font-size: 20px !important;
            margin-bottom: 12px !important;
          }
          .blog-card-excerpt {
            font-size: 14px !important;
            line-height: 1.6 !important;
            margin-bottom: 16px !important;
          }
        }
      `}</style>

      {/* Header */}
      <header className="blog-header" style={{
        backgroundColor: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '20px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <a
            href="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              textDecoration: 'none',
            }}
          >
            <img
              className="blog-logo"
              src="/ManuscriptMatchLogo.svg"
              alt="Manuscript Match Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div className="blog-logo-text" style={{
              fontSize: '24px',
              fontWeight: '600',
              color: theme.colors.primary,
              letterSpacing: '-0.01em',
            }}>
              Manuscript Match
            </div>
          </a>
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
          }}>
            <a
              href="/"
              className="blog-header-link"
              style={{
                color: theme.colors.textSecondary,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                transition: 'color 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = theme.colors.primary;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = theme.colors.textSecondary;
              }}
            >
              Home
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="blog-hero" style={{
          backgroundColor: theme.colors.backgroundAlt,
          padding: '80px 40px',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center',
          }}>
            <h1 className="blog-hero-title" style={{
              fontSize: '56px',
              fontWeight: '600',
              marginBottom: '24px',
              color: theme.colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: '1.1',
            }}>
              Manuscript Match Blog
            </h1>
            <p className="blog-hero-subtitle" style={{
              fontSize: '20px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
              maxWidth: '700px',
              margin: '0 auto',
            }}>
              Insights, tips, and stories from the literary community. Learn about beta reading, querying agents, and everything in between.
            </p>
          </div>
        </section>

        {/* Blog Content */}
        <section className="blog-content" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '80px 40px',
        }}>
          {/* Category Filters */}
          <div className="blog-filters" style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '48px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="blog-filter-btn"
                style={{
                  padding: '10px 20px',
                  borderRadius: theme.borderRadius.pill,
                  border: selectedCategory === category
                    ? `2px solid ${theme.colors.primary}`
                    : `1px solid ${theme.colors.border}`,
                  backgroundColor: selectedCategory === category
                    ? theme.colors.primaryLight
                    : theme.colors.white,
                  color: selectedCategory === category
                    ? theme.colors.primary
                    : theme.colors.textSecondary,
                  fontSize: '14px',
                  fontWeight: selectedCategory === category ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  if (selectedCategory !== category) {
                    e.currentTarget.style.borderColor = theme.colors.primary;
                    e.currentTarget.style.color = theme.colors.primary;
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== category) {
                    e.currentTarget.style.borderColor = theme.colors.border;
                    e.currentTarget.style.color = theme.colors.textSecondary;
                  }
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="blog-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '32px',
          }}>
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="blog-card"
                itemScope
                itemType="https://schema.org/BlogPosting"
                style={{
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.xlarge,
                  padding: '32px',
                  border: `1px solid ${theme.colors.border}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Category Badge & Icon */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                }}>
                  <span style={{
                    backgroundColor: theme.colors.primaryLight,
                    color: theme.colors.primaryDark,
                    padding: '4px 12px',
                    borderRadius: theme.borderRadius.pill,
                    fontSize: '12px',
                    fontWeight: '600',
                    letterSpacing: '0.02em',
                  }}>
                    {post.category}
                  </span>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    backgroundColor: theme.colors.primaryLight,
                    borderRadius: theme.borderRadius.large,
                    color: theme.colors.primary,
                  }}>
                    {post.icon}
                  </div>
                </div>

                {/* Hidden link for SEO */}
                <link itemProp="url" href={`${canonicalBlogUrl}/${post.slug}`} />

                {/* Title */}
                <h2 className="blog-card-title" itemProp="headline" style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                  letterSpacing: '-0.01em',
                  lineHeight: '1.3',
                }}>
                  <a href={`/blog/${post.slug}`} style={{ color: 'inherit', textDecoration: 'underline', textDecorationThickness: '1px', textUnderlineOffset: '4px' }}>
                    {post.title}
                  </a>
                </h2>

                {/* Excerpt */}
                <p className="blog-card-excerpt" itemProp="description" style={{
                  fontSize: '15px',
                  lineHeight: '1.7',
                  color: theme.colors.textSecondary,
                  marginBottom: '20px',
                }}>
                  {post.excerpt}
                </p>

                {/* Meta Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '16px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}>
                    <span itemProp="author" style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                      fontWeight: '500',
                    }}>
                      {post.author}
                    </span>
                    <time itemProp="datePublished" dateTime={new Date(post.date).toISOString()} style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                    }}>
                      {post.date}
                    </time>
                  </div>
                  <span style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    fontWeight: '500',
                  }}>
                    {post.readTime}
                  </span>
                </div>
              </article>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: theme.colors.textSecondary,
            }}>
              <p style={{ fontSize: '18px' }}>No posts found in this category yet.</p>
            </div>
          )}

          {/* Newsletter Signup CTA */}
          <div style={{
            marginTop: '80px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.xlarge,
            padding: '48px',
            textAlign: 'center',
            border: `1px solid ${theme.colors.border}`,
          }}>
            <h3 style={{
              fontSize: '32px',
              fontWeight: '600',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
              letterSpacing: '-0.01em',
            }}>
              Stay Updated
            </h3>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: '1.6',
            }}>
              Join our waitlist to be the first to know when Manuscript Match launches. Get exclusive updates and early access to the platform.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              style={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: theme.borderRadius.medium,
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Join the Waitlist
            </button>
          </div>
        </section>
      </main>

      <Footer />
      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};
